import type {Relation} from "typeorm";
import {Brackets, Column, Entity, JoinColumn, ManyToOne, OneToMany, OneToOne, RelationId} from "typeorm";
import {BaseEntity} from "@/modules/base/entity";
import {Team} from "@/modules/team/entity/Team";
import {Matchday} from "@/modules/competitions/entity/Matchday";
import {MatchResultEnum} from "@/modules/base/enums";
import {Season} from "../../season/entity";
import {Incident} from "@/modules/match/entity/Incident";
import {StadiumLog} from "@/modules/stadium/entity";
import {FormationEnum} from "@/modules/team/enums";

@Entity({orderBy: {id: 'ASC'}})
export class Match extends BaseEntity {
  @ManyToOne(() => Team)
  homeTeam: Relation<Team>;

  @ManyToOne(() => Team)
  awayTeam: Relation<Team>;

  @Column({type: 'varchar', enum: FormationEnum, nullable: true})
  homeFormation?: FormationEnum;

  @Column({type: 'varchar', enum: FormationEnum, nullable: true})
  awayFormation?: FormationEnum;

  @ManyToOne(() => Matchday, matchday => matchday.matches)
  matchday: Relation<Matchday>;

  // this is the first / second lag connection of matches
  @OneToOne(() => Match, {nullable: true})
  @JoinColumn()
  connectedMatch?: Match;

  @OneToMany(() => Incident, r => r.match)
  incidents: Incident[];

  @Column({type: 'boolean'})
  tooFewPlayers = false;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homeGoals?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awayGoals?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homePenaltyGoals?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awayPenaltyGoals?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homeSkill?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homeEffectiveSkill?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awaySkill?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awayEffectiveSkill?: number;

  @Column({type: "varchar", enum: MatchResultEnum, nullable: true})
  result?: MatchResultEnum;

  @Column({type: "boolean"})
  extraTime = false;

  @ManyToOne(() => Team, {nullable: true})
  advancedTeam?: Relation<Team>;

  @RelationId((m: Match) => m.advancedTeam)
  advancedTeamId?: number;

  @OneToOne(() => StadiumLog, r => r.match, {eager: true})
  stadiumLog?: Relation<StadiumLog>;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homeChances?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awayChances?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  homePossession?: number;

  @Column({type: 'integer', unsigned: true, nullable: true})
  awayPossession?: number;

  @Column({type: 'boolean'})
  penalty = false;

  @Column({type: 'boolean'})
  needDecision = false;

  static findPrev(season: Season, team: Team) {
    return this
      .createQueryBuilder('match')
      .leftJoinAndSelect('match.homeTeam', 'homeTeam')
      .leftJoinAndSelect('match.awayTeam', 'awayTeam')
      .leftJoinAndSelect('match.matchday', 'matchday')
      .leftJoinAndSelect('matchday.division', 'division')
      .leftJoinAndSelect('division.competition', 'competition')
      .where(
        new Brackets(query => {
          query
            .where('homeTeam.id = :teamId', {teamId: team.id})
            .orWhere('awayTeam.id = :teamId', {teamId: team.id});
        })
      )
      .andWhere('matchday.finished = :finished', {finished: true})
      .orderBy('matchday.scheduledAt', 'DESC')
      .getOne();
  }

  static findNext(season: Season, team: Team) {
    return this
      .createQueryBuilder('match')
      .leftJoinAndSelect('match.homeTeam', 'homeTeam')
      .leftJoinAndSelect('match.awayTeam', 'awayTeam')
      .leftJoinAndSelect('match.matchday', 'matchday')
      .leftJoinAndSelect('matchday.division', 'division')
      .leftJoinAndSelect('division.competition', 'competition')
      .where(
        new Brackets(query => {
          query
            .where('homeTeam.id = :teamId', {teamId: team.id})
            .orWhere('awayTeam.id = :teamId', {teamId: team.id});
        })
      )
      .andWhere('matchday.finished = :finished', {finished: false})
      .orderBy('matchday.scheduledAt', 'ASC')
      .getOne();
  }

  get isUserWin() {
    return this.result === MatchResultEnum.HOME_WIN && this.homeTeam.user
      || this.result === MatchResultEnum.AWAY_WIN && this.awayTeam.user;
  }

  get isUserLose() {
    return this.result === MatchResultEnum.AWAY_WIN && this.homeTeam.user
      || this.result === MatchResultEnum.HOME_WIN && this.awayTeam.user;
  }

  get userTeam() {
    return this.homeTeam && this.homeTeam.user ? this.homeTeam : this.awayTeam;
  }

  get oppTeam() {
    return this.homeTeam && this.homeTeam.user ? this.awayTeam : this.homeTeam;
  }
}
